"use client";

import { useEffect, useRef, useState } from "react";

import { usePathname, useRouter } from "next/navigation";

import { Minus, SquarePen, Sparkles } from "lucide-react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";

import { ChatComposer } from "@/components/chat/ChatComposer";
import { ChatMessages } from "@/components/chat/ChatMessages";
import { LogoHeader } from "@/components/navbar/LogoHeader";
import { GenderLink } from "@/components/navbar/navigation/GenderLink";
import { useChat } from "@/contexts/ChatContext";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { useUserGender } from "@/hooks/useUserGender";
import { isSeoLandingPagePath, LANDING_PAGE_PATH } from "@/lib/landing-page";
import { RESULTS_PATH_PREFIX } from "@/lib/route-paths";
import { colors, tailwindBreakpoints, zIndex } from "@/styles/design-tokens";
import {
  DISPLAY_GENDER_OPTIONS,
  displayGenderToCategoryToken,
  mapGenderToDisplay,
  type DisplayGender,
} from "@/utils/genderMapping";

export type ChatDisplayMode = "fullscreen" | "floating" | "minimized";

// Slight upward nudge keeps the 44px mobile icon targets visually centered in the compact header.
const MOBILE_HEADER_ACTION_OFFSET_CLASS = "top-[43%]";

interface ChatWindowProps {
  chatId: string;
  displayMode: ChatDisplayMode;
  onDisplayModeChange?: (mode: ChatDisplayMode) => void;
  hasUnreadMessages?: boolean;
  className?: string;
}

// Sub-components for cleaner rendering
interface SwipeHandleProps {
  onTouchStart?: (e: React.TouchEvent) => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
}

function SwipeHandle({ onTouchStart, onTouchEnd }: SwipeHandleProps) {
  return (
    <div className="pointer-events-none relative flex h-1 w-full items-center justify-center pb-1.5 pt-2.5 sm:hidden">
      <div
        className="pointer-events-auto absolute left-1/2 top-1/2 h-11 w-28 -translate-x-1/2 -translate-y-1/2"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none h-1 w-10 rounded-full"
        style={{ backgroundColor: colors.divider.default }}
      />
    </div>
  );
}

export function ChatWindow({
  chatId,
  displayMode,
  onDisplayModeChange,
  hasUnreadMessages,
  className = "",
}: ChatWindowProps) {
  const router = useRouter();
  const pathname = usePathname();
  const {
    activeChatId,
    messages,
    isStreaming,
    error,
    setActiveChatId,
    loadChatHistory,
    sendMessage,
    clearChat,
    setChatCategory,
  } = useChat();
  const userGender = useUserGender();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const touchStartY = useRef<number | null>(null);
  const hasUserInteracted = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingUndoChatIdRef = useRef<string | null>(null);
  const newChatIdRef = useRef<string | null>(null);
  const hasEverHadUserMessagesRef = useRef(false);
  const skipHistoryLoadRef = useRef<string | null>(null);
  const manualGenderChatIdsRef = useRef<Set<string>>(new Set());
  const chatGenderMapRef = useRef<Map<string, DisplayGender>>(new Map());

  const [localInputValue, setLocalInputValue] = useState("");
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [showHistoryLink, setShowHistoryLink] = useState(false);
  const [selectedGender, setSelectedGender] = useState<DisplayGender>("Both (M+W)");

  const hasUserMessages = messages.some((message) => message.role === "user");
  const lastMessage = messages[messages.length - 1];
  const isConversationStreaming =
    isStreaming || (lastMessage?.role === "assistant" && lastMessage.isStreaming);
  const isResultsPage = pathname.startsWith(RESULTS_PATH_PREFIX);
  const isSeoLandingPage = isSeoLandingPagePath(pathname);
  const isDesktopViewport = () =>
    typeof window !== "undefined" &&
    window.matchMedia(`(min-width: ${tailwindBreakpoints.sm}px)`).matches;

  useEffect(() => {
    if (!activeChatId) return;
    const isManual = manualGenderChatIdsRef.current.has(activeChatId);
    const storedGender = chatGenderMapRef.current.get(activeChatId);
    if (isManual) {
      if (storedGender) {
        setSelectedGender(storedGender);
        return;
      }
    }

    if (userGender) {
      const mappedGender = mapGenderToDisplay(userGender);
      chatGenderMapRef.current.set(activeChatId, mappedGender);
      setSelectedGender(mappedGender);
      return;
    }

    if (storedGender) {
      setSelectedGender(storedGender);
      return;
    }

    const fallbackGender: DisplayGender = "Both (M+W)";
    chatGenderMapRef.current.set(activeChatId, fallbackGender);
    setSelectedGender(fallbackGender);
  }, [activeChatId, hasUserMessages, userGender]);

  useEffect(() => {
    if (!activeChatId || hasUserMessages) return;
    setChatCategory(activeChatId, displayGenderToCategoryToken(selectedGender));
  }, [activeChatId, hasUserMessages, selectedGender, setChatCategory]);

  useEffect(() => {
    if (hasUserMessages) {
      hasEverHadUserMessagesRef.current = true;
    }
  }, [hasUserMessages]);

  // Ensure "fixed" overlays truly cover the viewport (avoid transformed-parent containing blocks).
  useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  // Helper to scroll messages to bottom with cleanup support
  const scrollToBottom = (delay = 0) => {
    const scroll = () => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    };
    if (delay > 0) {
      // Clear any pending scroll timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(scroll, delay);
    } else {
      scroll();
    }
  };

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Initialize and load chat when chatId changes
  useEffect(() => {
    if (!chatId) return;
    let isMounted = true;
    setActiveChatId(chatId);
    hasUserInteracted.current = false;
    if (skipHistoryLoadRef.current === chatId) {
      skipHistoryLoadRef.current = null;
      return () => {
        isMounted = false;
      };
    }
    loadChatHistory(chatId).then(() => {
      if (isMounted) {
        scrollToBottom(100);
      }
    });
    return () => {
      isMounted = false;
    };
  }, [chatId, setActiveChatId, loadChatHistory]);

  useEffect(() => {
    // Ref is intentional here; we only want to react to chatId changes.
    if (chatId !== newChatIdRef.current) {
      setShowHistoryLink(false);
    }
  }, [chatId]);

  // Handle display mode changes: scroll to bottom and lock body scroll
  useEffect(() => {
    const isOpen = displayMode === "floating" || displayMode === "fullscreen";
    if (!isOpen) return;

    // Scroll to bottom when chat window opens
    scrollToBottom(50);
  }, [displayMode]);

  // Lock body scroll when the chat is fullscreen (any viewport).
  useBodyScrollLock({ key: "chat-window:fullscreen", enabled: displayMode === "fullscreen" });

  // Lock body scroll when the chat is in floating sheet mode on small viewports (where the backdrop shows).
  // NOTE: Uses `sm` breakpoint (not `md`) to match the backdrop's `sm:hidden` CSS class.
  useBodyScrollLock({
    key: "chat-window:mobile-floating",
    enabled: displayMode === "floating",
    maxWidthPx: tailwindBreakpoints.sm - 1,
  });

  // Auto-scroll when streaming or user is actively chatting
  useEffect(() => {
    if (displayMode !== "minimized" && (isStreaming || hasUserInteracted.current)) {
      scrollToBottom();
    }
  }, [messages, displayMode, isStreaming]);

  // Swipe gesture handlers for mobile (vertical swipes only)
  const touchStartX = useRef<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartY.current === null || touchStartX.current === null) return;

    const deltaY = touchStartY.current - e.changedTouches[0].clientY;
    const deltaX = touchStartX.current - e.changedTouches[0].clientX;
    const swipeThreshold = 50;

    // Only trigger vertical swipe actions if the swipe is primarily vertical
    // (prevents interference with horizontal scrolling in chat content)
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > swipeThreshold) {
      if (deltaY > 0) {
        if (displayMode === "floating") {
          onDisplayModeChange?.("fullscreen");
        }
      } else {
        if (displayMode === "fullscreen") {
          onDisplayModeChange?.("floating");
        } else {
          onDisplayModeChange?.("minimized");
        }
      }
    }

    touchStartY.current = null;
    touchStartX.current = null;
  };
  const renderMessages = (classNameOverride?: string) => (
    <div className="flex min-h-0 flex-1 flex-col">
      <ChatMessages
        messages={messages}
        chatId={chatId}
        containerRef={messagesContainerRef}
        className={classNameOverride ?? "px-3 py-2 sm:px-4 sm:py-3"}
        showHistoryLink={showHistoryLink}
        showHistoryLinkOnEmpty={showHistoryLink || hasEverHadUserMessagesRef.current}
        emptyStateFooter={
          displayMode === "floating" ? (
            <div className="mx-auto flex w-full max-w-[320px] flex-col items-start sm:max-w-[360px]">
              <p
                className="mb-2 text-[10px] font-normal uppercase tracking-[1.1172px]"
                style={{ color: colors.text.grayLightest }}
              >
                Category
              </p>
              <div className="flex w-full flex-wrap items-center gap-x-[28px] gap-y-3">
                {DISPLAY_GENDER_OPTIONS.map((gender) => (
                  <GenderLink
                    key={gender}
                    text={gender}
                    isSelected={selectedGender === gender}
                    onClick={() => {
                      if (!activeChatId) return;
                      manualGenderChatIdsRef.current.add(activeChatId);
                      chatGenderMapRef.current.set(activeChatId, gender);
                      setSelectedGender(gender);
                      const categoryToken = displayGenderToCategoryToken(gender);
                      setChatCategory(activeChatId, categoryToken);
                    }}
                  />
                ))}
              </div>
            </div>
          ) : undefined
        }
      />
      {error && (
        <div
          className="mx-3 mb-3 mt-3 rounded p-2 text-xs sm:mx-4"
          style={{
            backgroundColor: colors.feedback.errorBackground,
            color: colors.feedback.errorTextPrimary,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );

  const renderComposer = (classNameOverride?: string) => (
    <div
      className={`flex-shrink-0 border-t ${classNameOverride ?? "px-3 pb-2 pt-2 sm:px-3 sm:py-3"}`}
      style={{ borderColor: colors.divider.default }}
    >
      <ChatComposer
        ref={textareaRef}
        variant="compact"
        value={localInputValue}
        onChange={setLocalInputValue}
        onSend={handleSendMessage}
        isStreaming={isStreaming}
      />
    </div>
  );

  const renderAssistantHeader = () => (
    <div
      className="flex items-start justify-between border-b px-3 py-0.5 sm:items-center sm:px-4 sm:py-3"
      style={{ borderColor: colors.divider.default }}
    >
      <div className="flex items-center gap-3">
        <Sparkles className="h-4 w-4" style={{ color: colors.text.black }} />
        <span className="text-sm font-medium" style={{ color: colors.text.black }}>
          Shopping Assistant
        </span>
      </div>
      <div className="flex items-center gap-2 sm:gap-0">
        {hasUserMessages && (
          // Mobile keeps a compact visual slot and lets the 44px button overflow it.
          // If an ancestor starts clipping overflow, revisit this header layout.
          <div className="relative h-8 w-9 sm:h-11 sm:w-11">
            <button
              type="button"
              onClick={handleNewChat}
              disabled={isConversationStreaming}
              className={`absolute left-1/2 ${MOBILE_HEADER_ACTION_OFFSET_CLASS} flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full transition-colors ${!isConversationStreaming ? "sm:hover:bg-gray-100" : ""} sm:left-0 sm:top-0 sm:translate-x-0 sm:translate-y-0`}
              title={isConversationStreaming ? "Wait for response to complete" : "New chat"}
              aria-label="Start a new chat"
              aria-disabled={isConversationStreaming}
              style={{
                opacity: isConversationStreaming ? 0.4 : 1,
                cursor: isConversationStreaming ? "not-allowed" : "pointer",
              }}
            >
              <SquarePen
                className="h-4 w-4 sm:h-5 sm:w-5"
                style={{ color: colors.text.accentMdGray }}
              />
            </button>
          </div>
        )}
        <div className="relative h-8 w-9 sm:h-11 sm:w-11">
          <button
            type="button"
            onClick={() => onDisplayModeChange?.("minimized")}
            className={`absolute left-1/2 ${MOBILE_HEADER_ACTION_OFFSET_CLASS} flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full transition-colors sm:hover:bg-gray-100 sm:left-0 sm:top-0 sm:translate-x-0 sm:translate-y-0`}
            title="Minimize"
            aria-label="Minimize chat window"
          >
            <Minus className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: colors.text.accentMdGray }} />
          </button>
        </div>
        {/* TODO: Consider adding an "open in new tab" icon here that navigates to /chats/[id] */}
      </div>
    </div>
  );

  const handleSendMessage = async () => {
    const message = localInputValue.trim();
    if (!message || isStreaming) return;
    console.debug("[ChatWindow] Sending message", {
      activeChatId,
      selectedGender,
      hasUserMessages,
    });
    if (showHistoryLink) {
      setShowHistoryLink(false);
      pendingUndoChatIdRef.current = null;
      newChatIdRef.current = null;
    }
    setLocalInputValue("");
    hasUserInteracted.current = true;
    const categoryToken = displayGenderToCategoryToken(selectedGender);
    await sendMessage(message, true, hasUserMessages ? undefined : categoryToken);
  };

  const handleNewChat = () => {
    if (isConversationStreaming || !activeChatId) return;
    console.debug("[ChatWindow] New chat requested", { activeChatId, selectedGender });
    const previousChatId = activeChatId;
    const previousChatIdForUndo = previousChatId;
    pendingUndoChatIdRef.current = previousChatIdForUndo;

    clearChat();
    setLocalInputValue("");
    hasUserInteracted.current = false;
    const fallbackGender = userGender ? mapGenderToDisplay(userGender) : "Both (M+W)";
    setSelectedGender(fallbackGender);

    const newChatId = uuidv4();
    newChatIdRef.current = newChatId;
    skipHistoryLoadRef.current = newChatId;
    manualGenderChatIdsRef.current.delete(newChatId);
    chatGenderMapRef.current.set(newChatId, fallbackGender);
    setActiveChatId(newChatId);
    setShowHistoryLink(true);

    if (!isResultsPage && !isSeoLandingPage) {
      router.push(LANDING_PAGE_PATH);
    }

    if ((isResultsPage || isSeoLandingPage) && isDesktopViewport()) {
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    }

    toast.success("New conversation started", {
      action: {
        label: "Undo",
        onClick: () => {
          if (pendingUndoChatIdRef.current !== previousChatIdForUndo) {
            return;
          }
          const undoChatId = previousChatIdForUndo;
          pendingUndoChatIdRef.current = null;
          newChatIdRef.current = null;
          setShowHistoryLink(false);
          setActiveChatId(undoChatId);
          loadChatHistory(undoChatId).catch((err) => {
            console.error("Failed to restore chat history:", err);
            toast.error("Failed to restore previous conversation");
          });
        },
      },
      duration: 4000,
    });
  };

  // Minimized state - show as bubble
  if (displayMode === "minimized") {
    return (
      <button
        onClick={() => onDisplayModeChange?.("floating")}
        className="fixed bottom-4 left-4 right-4 z-50 flex items-center justify-center gap-2 rounded-full px-5 py-3 shadow-lg transition-transform hover:scale-105 sm:left-auto sm:right-6 sm:w-auto"
        style={{ backgroundColor: colors.stroke.pureBlack }}
        title="Open chat"
      >
        <Sparkles className="h-5 w-5 text-white" />
        <span className="whitespace-nowrap text-sm font-medium text-white">Shopping Assistant</span>
        {isStreaming && (
          <div className="absolute -right-1 -top-1 h-4 w-4 animate-pulse rounded-full bg-blue-500" />
        )}
        {!isStreaming && hasUnreadMessages && (
          <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500">
            <span className="text-[10px] font-bold text-white">1</span>
          </div>
        )}
      </button>
    );
  }

  // Floating state - Instagram-style bottom sheet on mobile, floating card on desktop
  if (displayMode === "floating") {
    if (!portalTarget) return null;
    return createPortal(
      <>
        {/* Mobile backdrop overlay */}
        <div
          className="fixed inset-0 bg-black/30 sm:hidden"
          style={{ zIndex: zIndex.chatBackdrop }}
          onClick={() => onDisplayModeChange?.("minimized")}
          aria-hidden="true"
        />
        <div
          className={`fixed flex flex-col bg-white shadow-2xl
            left-0 right-0 bottom-0 w-full rounded-t-[20px]
           h-[min(55vh,420px)]
            sm:left-auto sm:right-6 sm:bottom-4 sm:w-[380px] sm:rounded-2xl
            sm:h-[min(520px,calc(100vh-120px))] ${className}`}
          style={{
            border: `1px solid ${colors.divider.default}`,
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
            zIndex: zIndex.chatWindow,
          }}
        >
          <div className="flex-shrink-0">
            <SwipeHandle onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} />
            {renderAssistantHeader()}
          </div>
          {renderMessages()}
          {renderComposer()}
        </div>
      </>,
      portalTarget
    );
  }

  if (displayMode === "fullscreen") {
    return (
      <div
        className={`fixed inset-0 z-50 flex flex-col ${className}`}
        style={{
          backgroundColor: colors.accent.veryLightBlueGray,
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <LogoHeader backgroundColor={colors.accent.veryLightBlueGray} showBorder={false} />
        <div className="flex min-h-0 flex-1 flex-col rounded-t-[20px] bg-white">
          <SwipeHandle onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} />
          {renderAssistantHeader()}
          {renderMessages("px-4 py-3 sm:px-6 sm:py-4")}
          {renderComposer("px-4 pb-3 pt-3 sm:px-6 sm:py-4")}
        </div>
      </div>
    );
  }

  return null;
}
